import { getFacebookEventId, getLocalId } from 'Lib/AppUtils'
import { getTrackingIds } from 'Lib/trackingConfig'
import { Selectors } from 'Reducers'
import { Store } from 'redux'
import { trackCustomSignalsGatewayEvent, trackSignalsGatewayEvent } from 'Services/SignalsGateway.service'

const DISABLE_ON_DEV = true // Set to false to enable Facebook in development

type AnyFunction = (...args: any[]) => any

// Per-domain pixel ids (see Lib/trackingConfig.ts).
const META_PIXEL_IDS = getTrackingIds().metaPixelIds

// We noticed that the cookie consent state is stacking up on Facebook Pixel.
// This means that if we revoke consent multiple times, we have to grant it the same number of times
// to get it working again. To avoid this issue, we track the current consent state ourselves.
let cookieConsentGranted: boolean | null = null

// fbevents.js consumes the queued initial consent revoke and PARKS everything
// queued behind it — including a consent grant that was queued before the
// script loaded. A parked grant can never unpark itself, so returning visitors
// whose saved cookie consent is applied before fbevents.js finishes loading
// (cold cache / slow network) deadlock and send no pixel events for the whole
// session. Verified live on my.bodyfast.app with a delayed fbevents.js:
// consent granted, SDK loaded, queue parked, zero /tr requests. Replaying the
// granted state with a direct call on the loaded SDK unparks the queue; a
// duplicate grant is harmless. Same bug class as the Signals Gateway consent
// deadlock in SignalsGateway.service.ts.
const replayFacebookConsentState = () => {
  if (cookieConsentGranted === true) {
    window.fbq?.('consent', 'grant')
  }
}

let store: Store | null = null
let currentEmail: string | null = null

export const revokeFacebookConsent = () => {
  if (__DEV__ && DISABLE_ON_DEV) {
    console.log('Revoking Facebook Pixel consent')
    return
  }

  // We noticed that the cookie consent state is stacking up on Facebook Pixel.
  // This means that if we revoke consent multiple times, we have to grant it the same number of times
  // to get it working again. To avoid this issue, we track the current consent state ourselves.
  if (cookieConsentGranted === false) {
    return
  }

  cookieConsentGranted = false
  window.fbq?.('consent', 'revoke')
}

export const grantFacebookConsent = () => {
  if (__DEV__ && DISABLE_ON_DEV) {
    console.log('Granting Facebook Pixel consent')
    return
  }

  // We noticed that the cookie consent state is stacking up on Facebook Pixel.
  // This means that if we revoke consent multiple times, we have to grant it the same number of times
  // to get it working again. To avoid this issue, we track the current consent state ourselves.
  if (cookieConsentGranted === true) {
    return
  }

  cookieConsentGranted = true
  window.fbq?.('consent', 'grant')

  // Pre-consent PageViews are suppressed (see trackFacebookEvent), so fire one for
  // the current page on grant. Deferred a macrotask so SGW consent (set next in
  // handleConsentChange) lands first; shares one eventID across Pixel + SGW.
  setTimeout(() => trackFacebookPageView(), 0)
}

// Add the Advance Matching Data to the pixel
// More info: https://developers.facebook.com/docs/facebook-pixel/advanced/advanced-matching
// For some peculiar reasons, you cannot use fbq('set', 'userData', {}), as it complains about the pixel_id already being set
// So we have to re-init the pixel with the userData. Even more peculiar is that this can only be done once. Second time
// calls will ignore the new data. So we have to do this in a oneshot manner.
// See also: https://medium.com/@w.anatolii/meta-init-how-to-send-user-data-to-facebook-568a9ceac971
const setAdvancedMatching = (email: string, gender?: 'f' | 'm' | null, countryCode?: string | null) => {
  if (
    !(__DEV__ && DISABLE_ON_DEV) &&
    window.fbq &&
    currentEmail !== email
  ) {
    currentEmail = email
    META_PIXEL_IDS.forEach(id => {
      window.fbq?.('init', id, { em: email, external_id: getLocalId(), ge: gender, country: countryCode })
    })
  }
}

const withStoredUserEmail = <Func extends AnyFunction>(
  fn: Func,
) => {
  return (...args: Parameters<Func>) => {
    if (!store) {
      return fn(...args)
    }

    const state = store.getState()
    const email = Selectors.getEmail(state)
    const onboardingEmail = Selectors.getOnboardingEmail(state)
    const gender = Selectors.getGender(state) ?? (localStorage.getItem('gender') as IGender | null)
    const countryCode = localStorage.getItem('countryCode')

    const trackedEmail = email || onboardingEmail

    if (trackedEmail) {
      setAdvancedMatching(trackedEmail.trim().toLowerCase(), gender === 'o' ? null : gender, countryCode?.toLowerCase())
    }

    fn(...args)
  }
}

// `eventId` lets the caller pin a stable, server-shared dedup id (e.g. the
// Stripe subscription id for Purchase). When omitted we fall back to a fresh
// per-call uuid, which only dedups the Pixel against the Signals Gateway —
// NOT against any server-side CAPI event. Server-side events (e.g. the Stripe
// webhook Meta CAPI Purchase) must use the same id for Meta to deduplicate.
export const trackFacebookEvent = withStoredUserEmail((event: string, data: Record<string, any> = {}, eventId?: string) => {
  if (__DEV__ && DISABLE_ON_DEV) {
    console.log(`Tracking Facebook Pixel event: ${event}`, data)
    return
  }

  const extraData = { eventID: eventId ?? getFacebookEventId() }

  // Skip pre-consent PageViews: fbevents flushes them on grant with the grant-time
  // URL (no per-event URL override). grantFacebookConsent fires one for the real page.
  if (event === 'PageView' && !cookieConsentGranted) return

  // Don't pass fbc/fbp in custom_data (3rd arg): they're user_data matching params,
  // so the SDKs ignore them here (verified no-op). Each SDK reads its own click
  // cookie (_fbc/_fbp, _cbc/_cbp). fbc is sent server-side for the CAPI Purchase.
  window?.fbq?.('track', event, data, extraData)
  trackSignalsGatewayEvent(event, data, extraData)
})

export const trackCustomFacebookEvent = withStoredUserEmail((event: string, data: Record<string, any> = {}) => {
  if (__DEV__ && DISABLE_ON_DEV) {
    console.log(`Tracking Facebook Pixel custom event: ${event}`, data)
    return
  }

  const extraData = { eventID: getFacebookEventId() }

  // See trackFacebookEvent: fbc/fbp deliberately omitted (no-op in custom_data).
  window?.fbq?.('trackCustom', event, data, extraData)
  trackCustomSignalsGatewayEvent(event, data, extraData)
})

export const trackFacebookPageView = withStoredUserEmail(() => {
  if (__DEV__ && DISABLE_ON_DEV) {
    console.log('Tracking Facebook Pixel page view')
    return
  }

  trackFacebookEvent('PageView')
})

export function handleFacebookStepTrack(stepId: string) {
  if (__DEV__ && DISABLE_ON_DEV) {
    return
  }

  if (stepId === 'gender') {
    trackCustomFacebookEvent('commitGender')
  } else if (stepId === 'reinforcement-scientific') {
    trackCustomFacebookEvent('commitReinforcementScientific')
  } else if (stepId === 'bmi') {
    trackCustomFacebookEvent('commitSummary')
  } else if (stepId === 'email') {
    trackCustomFacebookEvent('commitNewsletter')
  } else if (stepId === 'receive-email') {
    trackCustomFacebookEvent('commitReceiveNewsletter')
  }
}

export function initFacebookPixel(newStore: Store) {
  if (__DEV__ && DISABLE_ON_DEV) return
  if (window.fbq) return

  store = newStore

  const fbq: FacebookPixelFn = function (...args: any[]) {
    if (fbq.callMethod) {
      fbq.callMethod(...args)
    } else {
      fbq.queue?.push(args)
    }
  }

  fbq.queue = []
  fbq.loaded = true
  fbq.version = '2.0'
  fbq.push = (...args: any[]) => fbq(...args)

  window.fbq = fbq
  window._fbq = fbq

  // Setting both flags to true is what makes our manually-tracked per-route
  // PageViews send with their eventID (needed for deduplication against the
  // server-side CAPI / Signals Gateway PageView). Set before fbevents.js loads.
  fbq.disablePushState = true
  fbq.allowDuplicatePageViews = true

  const script = document.createElement('script')
  script.async = true
  script.src = 'https://connect.facebook.net/en_US/fbevents.js'
  script.onload = replayFacebookConsentState

  const firstScript = document.getElementsByTagName('script')[0]
  firstScript?.parentNode?.insertBefore(script, firstScript)

  revokeFacebookConsent() // According to the docs, we need to revoke the consent before initializing: https://developers.facebook.com/docs/facebook-pixel/implementation/gdpr/

  META_PIXEL_IDS.forEach(id => {
    window.fbq?.('set', 'autoConfig', false, id)
    window.fbq?.('init', id)
  })
}
